// Polished product-preview panels for the vertical storefronts. Rendered as
// HTML+Tailwind (not raw screenshots) so the visuals stay pixel-perfect on
// any device, load instantly, and match the storefront's dark palette.
import {
  Bug, Snowflake, House, Heart, Warning, CheckCircle, MapPin, Sparkle,
  Pulse, Camera, CloudSun, CreditCard, Wrench, PaperPlaneTilt, TreeEvergreen, Package,
  Drop, Lightning, PlugsConnected, ChartLineUp, Clock, PaintRoller,
  Calendar,
} from "@phosphor-icons/react";

// ─── Shared frame ─────────────────────────────────────────
const Frame = ({ children, accent, testid }) => (
  <div className="rounded-2xl overflow-hidden shadow-2xl border border-white/10" data-testid={testid}>
    {/* macOS-style titlebar */}
    <div className="bg-slate-950 px-3 py-2 flex items-center gap-2 border-b border-white/5">
      <div className="flex gap-1.5">
        <div className="w-2.5 h-2.5 rounded-full bg-red-500/70"/>
        <div className="w-2.5 h-2.5 rounded-full bg-amber-500/70"/>
        <div className="w-2.5 h-2.5 rounded-full bg-emerald-500/70"/>
      </div>
      <div className="flex-1 flex justify-center">
        <div className="px-3 py-0.5 rounded-md bg-slate-800 text-[10px] font-mono text-slate-400">
          fieldradar.pro / admin
        </div>
      </div>
      <div className="w-2.5 h-2.5 rounded-full" style={{ background: accent }}/>
    </div>
    {children}
  </div>
);

const KPI = ({ label, value, accent, sub, testid }) => (
  <div className="bg-slate-900/60 border border-white/5 rounded-lg px-3 py-2.5" data-testid={testid}>
    <div className="text-[8px] font-black uppercase tracking-widest text-slate-500">{label}</div>
    <div className="font-display font-black text-2xl leading-none mt-1" style={{ color: accent }}>{value}</div>
    {sub && <div className="text-[9px] text-slate-500 mt-0.5">{sub}</div>}
  </div>
);

// ─── Pest — Route Radar ────────────────────────────────────
const PestMockup = ({ accent }) => (
  <Frame accent={accent} testid="mockup-pest">
    <div className="bg-gradient-to-br from-slate-900 to-slate-950 p-4 text-white">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="h-8 w-8 rounded-lg flex items-center justify-center" style={{ background: `${accent}22`, color: accent }}>
            <MapPin weight="fill" size={16}/>
          </div>
          <div>
            <div className="font-display font-black text-sm leading-none">Route Radar</div>
            <div className="text-[9px] text-slate-400 mt-0.5">Tuesday · 4 stops sequenced</div>
          </div>
        </div>
        <div className="px-2 py-1 rounded text-[9px] font-black uppercase tracking-widest" style={{ background: `${accent}22`, color: accent }}>
          <Sparkle weight="fill" size={9} className="inline mr-0.5"/> AI optimized
        </div>
      </div>
      <div className="grid grid-cols-3 gap-1.5 mb-3">
        <KPI label="Overdue" value="1" accent="#ef4444" testid="pest-kpi-overdue"/>
        <KPI label="Today" value="4" accent={accent} testid="pest-kpi-today"/>
        <KPI label="This Wk" value="9" accent="#22c55e" testid="pest-kpi-week"/>
      </div>
      <div className="space-y-1.5">
        {[
          { n: 1, name: "Little Sicily Pizza", addr: "1420 Passyunk Ave", pest: "cockroaches", state: "overdue", eta: "8:00 AM" },
          { n: 2, name: "Twin Peaks Apts", addr: "22 Ridge Rd, Manayunk", pest: "ants", state: "today", eta: "9:45 AM" },
          { n: 3, name: "Riverside Cafe", addr: "3421 Girard Ave", pest: "rodents", state: "today", eta: "11:15 AM" },
          { n: 4, name: "Northside Warehouse", addr: "901 Erie Blvd, Camden", pest: "wasps", state: "today", eta: "1:30 PM" },
        ].map((s) => (
          <div key={s.n} className="flex items-center gap-2 bg-slate-800/60 border border-white/5 rounded-lg p-2">
            <div className="w-6 h-6 rounded-md flex items-center justify-center text-[10px] font-black flex-shrink-0" style={{ background: `${accent}33`, color: accent }}>{s.n}</div>
            <div className="min-w-0 flex-1">
              <div className="text-[11px] font-bold leading-tight truncate">{s.name}</div>
              <div className="text-[9px] text-slate-400 truncate">{s.addr} · {s.pest}</div>
            </div>
            <div className="text-right flex-shrink-0">
              <div className="text-[9px] font-mono text-slate-300">{s.eta}</div>
              {s.state === "overdue" && <div className="text-[8px] font-black uppercase tracking-widest text-red-400 flex items-center gap-0.5"><Warning weight="fill" size={8}/>Overdue</div>}
            </div>
          </div>
        ))}
      </div>
    </div>
  </Frame>
);

// ─── HVAC — Equipment Radar ────────────────────────────────
const HvacMockup = ({ accent }) => (
  <Frame accent={accent} testid="mockup-hvac">
    <div className="bg-gradient-to-br from-slate-900 to-slate-950 p-4 text-white">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="h-8 w-8 rounded-lg flex items-center justify-center" style={{ background: `${accent}22`, color: accent }}>
            <Snowflake weight="fill" size={16}/>
          </div>
          <div>
            <div className="font-display font-black text-sm leading-none">Equipment Radar</div>
            <div className="text-[9px] text-slate-400 mt-0.5">4 units tracked · 1 warranty expiring</div>
          </div>
        </div>
        <div className="px-2 py-1 rounded text-[9px] font-black uppercase tracking-widest border" style={{ borderColor: accent, color: accent }}>
          EPA 608 log · YTD
        </div>
      </div>
      <div className="grid grid-cols-3 gap-1.5 mb-3">
        <KPI label="Active" value="3" accent="#22c55e" testid="hvac-kpi-active"/>
        <KPI label="≤90d" value="1" accent="#f59e0b" testid="hvac-kpi-expiring"/>
        <KPI label="Expired" value="1" accent="#ef4444" testid="hvac-kpi-expired"/>
      </div>
      <div className="space-y-1.5">
        {[
          { name: "Roof Unit A · Carrier 4TSU2036A", addr: "501 Market St, Wilmington", warranty: "active", days: 1580 },
          { name: "Basement Furnace · Trane BAYS3T", addr: "1420 Passyunk Ave", warranty: "active", days: 620 },
          { name: "Attic Split · Lennox EL16XC1", addr: "901 Erie Blvd, Camden", warranty: "expiring", days: 74 },
          { name: "Rooftop 5-ton · York YCJF48S41", addr: "22 Ridge Rd, Manayunk", warranty: "expired", days: -228 },
        ].map((u, i) => (
          <div key={i} className="flex items-center gap-2 bg-slate-800/60 border border-white/5 rounded-lg p-2">
            <div className="w-6 h-6 rounded-md flex items-center justify-center flex-shrink-0" style={{ background: `${accent}33`, color: accent }}>
              <Snowflake weight="fill" size={12}/>
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-[11px] font-bold leading-tight truncate">{u.name}</div>
              <div className="text-[9px] text-slate-400 truncate">{u.addr}</div>
            </div>
            <div className="text-right flex-shrink-0">
              <div className={`text-[9px] font-black uppercase tracking-widest ${
                u.warranty === "expired" ? "text-red-400"
                : u.warranty === "expiring" ? "text-amber-400"
                : "text-emerald-400"}`}>{u.warranty}</div>
              <div className="text-[9px] font-mono text-slate-500">{u.days > 0 ? `${u.days}d left` : `${-u.days}d over`}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  </Frame>
);

// ─── Roofing — Job Radar ───────────────────────────────────
const RoofingMockup = ({ accent }) => (
  <Frame accent={accent} testid="mockup-roofing">
    <div className="bg-gradient-to-br from-slate-900 to-slate-950 p-4 text-white">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="h-8 w-8 rounded-lg flex items-center justify-center" style={{ background: `${accent}22`, color: accent }}>
            <House weight="fill" size={16}/>
          </div>
          <div>
            <div className="font-display font-black text-sm leading-none">Job Radar</div>
            <div className="text-[9px] text-slate-400 mt-0.5">4 jobs · 2 storm-flagged · $84.4K in play</div>
          </div>
        </div>
        <div className="px-2 py-1 rounded text-[9px] font-black uppercase tracking-widest inline-flex items-center gap-0.5" style={{ background: `${accent}22`, color: accent }}>
          <CloudSun weight="fill" size={10}/> NWS live
        </div>
      </div>
      <div className="grid grid-cols-4 gap-1.5 mb-3">
        <KPI label="Leads" value="1" accent={accent} testid="roof-kpi-leads"/>
        <KPI label="Proposal" value="1" accent="#f59e0b" testid="roof-kpi-proposal"/>
        <KPI label="Sched" value="1" accent="#0ea5e9" testid="roof-kpi-sched"/>
        <KPI label="Active" value="1" accent="#22c55e" testid="roof-kpi-active"/>
      </div>
      <div className="space-y-1.5">
        {[
          { name: "Rodriguez Family", addr: "2214 W Oregon Ave", status: "proposal", amt: "$14,200", storm: "Hail 1.25in · Feb 12" },
          { name: "Highland Estates HOA", addr: "88 Highland Cir, Cherry Hill", status: "scheduled", amt: "$5,800", storm: null },
          { name: "Miller Residence", addr: "3421 Girard Ave", status: "lead", amt: "—", storm: "Wind 68mph · Feb 18" },
          { name: "North Star Warehouse", addr: "901 Erie Blvd, Camden", status: "in_progress", amt: "$65,400", storm: null },
        ].map((j, i) => (
          <div key={i} className="flex items-center gap-2 bg-slate-800/60 border border-white/5 rounded-lg p-2">
            <div className="w-6 h-6 rounded-md flex items-center justify-center flex-shrink-0" style={{ background: `${accent}33`, color: accent }}>
              <House weight="fill" size={12}/>
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-[11px] font-bold leading-tight truncate">{j.name}</div>
              <div className="text-[9px] text-slate-400 truncate">
                {j.addr}
                {j.storm && <span className="ml-1.5 text-red-300 font-black">⚑ {j.storm}</span>}
              </div>
            </div>
            <div className="text-right flex-shrink-0">
              <div className="text-[9px] font-mono font-bold">{j.amt}</div>
              <div className="text-[8px] uppercase font-black tracking-widest text-slate-500">{j.status.replace("_", " ")}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  </Frame>
);

// ─── Home Care — Client Care Radar w/ vitals ──────────────
const HomeCareMockup = ({ accent }) => (
  <Frame accent={accent} testid="mockup-home-care">
    <div className="bg-gradient-to-br from-slate-900 to-slate-950 p-4 text-white">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="h-8 w-8 rounded-lg flex items-center justify-center" style={{ background: `${accent}22`, color: accent }}>
            <Heart weight="fill" size={16}/>
          </div>
          <div>
            <div className="font-display font-black text-sm leading-none">Client Care Radar</div>
            <div className="text-[9px] text-slate-400 mt-0.5">3 clients · 1 flagged today · 12 vitals this wk</div>
          </div>
        </div>
        <div className="px-2 py-1 rounded text-[9px] font-black uppercase tracking-widest inline-flex items-center gap-0.5" style={{ background: `${accent}22`, color: accent }}>
          <Pulse weight="fill" size={10}/> Vitals live
        </div>
      </div>
      <div className="grid grid-cols-3 gap-1.5 mb-3">
        <KPI label="Today" value="2" accent={accent} testid="hc-kpi-today"/>
        <KPI label="Flagged" value="1" accent="#ef4444" testid="hc-kpi-flagged"/>
        <KPI label="This wk" value="8" accent="#22c55e" testid="hc-kpi-week"/>
      </div>
      <div className="space-y-1.5">
        {[
          {
            name: "Mary Johnson", conds: "CHF · Type 2 diabetes", cadence: "daily",
            vitals: "BP 168/94 · HR 78 · O₂ 95%", flagged: true,
            flag: "Top blood pressure number is 168. Nurse should know today."
          },
          {
            name: "Bill Rodriguez", conds: "Post-op hip · Warfarin", cadence: "MWF",
            vitals: "BP 122/78 · HR 74 · O₂ 97%", flagged: false, flag: null
          },
          {
            name: "Ada Kim", conds: "Dementia — early", cadence: "weekly",
            vitals: "BP 118/72 · HR 68 · O₂ 98%", flagged: false, flag: null
          },
        ].map((c, i) => (
          <div key={i} className={`bg-slate-800/60 border rounded-lg p-2 ${c.flagged ? "border-red-500/50" : "border-white/5"}`}>
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 rounded-md flex items-center justify-center flex-shrink-0" style={{ background: `${accent}33`, color: accent }}>
                <Heart weight="fill" size={12}/>
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-[11px] font-bold leading-tight truncate">{c.name}</div>
                <div className="text-[9px] text-slate-400 truncate">{c.conds} · {c.cadence}</div>
              </div>
              <div className="text-[9px] font-mono text-slate-300 flex-shrink-0">{c.vitals}</div>
            </div>
            {c.flagged && (
              <div className="mt-1.5 pl-8 flex items-start gap-1.5 text-[9px] text-red-300 leading-snug">
                <Warning weight="fill" size={10} className="flex-shrink-0 mt-0.5"/>
                <span>{c.flag}</span>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  </Frame>
);

export default function ProductMockup({ storefrontKey, accent }) {
  if (storefrontKey === "pest_control") return <PestMockup accent={accent}/>;
  if (storefrontKey === "hvac") return <HvacMockup accent={accent}/>;
  if (storefrontKey === "roofing") return <RoofingMockup accent={accent}/>;
  if (storefrontKey === "home_health") return <HomeCareMockup accent={accent}/>;
  if (storefrontKey === "landlord") return <LandlordMockup accent={accent}/>;
  if (storefrontKey === "landscaping") return <LandscapingMockup accent={accent}/>;
  if (storefrontKey === "plumbing") return <PlumbingMockup accent={accent}/>;
  if (storefrontKey === "electrical") return <ElectricalMockup accent={accent}/>;
  if (storefrontKey === "handyman") return <HandymanMockup accent={accent}/>;
  if (storefrontKey === "vendors") return <VendorsMockup accent={accent}/>;
  return null;
}

// ─── Landlord — Repair Inbox with AI triage card ──────────
const LandlordMockup = ({ accent }) => (
  <Frame accent={accent} testid="mockup-landlord">
    <div className="bg-gradient-to-br from-slate-900 to-slate-950 p-4 text-white">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="h-8 w-8 rounded-lg flex items-center justify-center" style={{ background: `${accent}22`, color: accent }}>
            <House weight="fill" size={16}/>
          </div>
          <div>
            <div className="font-display font-black text-sm leading-none">Repair Inbox</div>
            <div className="text-[9px] text-slate-400 mt-0.5">Rental · 5346 Chew · 8A</div>
          </div>
        </div>
        <div className="px-2 py-1 rounded text-[9px] font-black uppercase tracking-widest" style={{ background: `${accent}22`, color: accent }}>
          <Sparkle weight="fill" size={9} className="inline mr-0.5"/> AI triaged
        </div>
      </div>
      <div className="grid grid-cols-3 gap-1.5 mb-3">
        <KPI label="New" value="0" accent="#a855f7" testid="ll-kpi-new"/>
        <KPI label="Triaged" value="1" accent={accent} testid="ll-kpi-triaged"/>
        <KPI label="Dispatched" value="2" accent="#22c55e" testid="ll-kpi-dispatched"/>
      </div>
      <div className="bg-slate-950/60 border border-white/5 rounded-lg overflow-hidden">
        <div className="p-2.5 border-b border-white/5">
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="text-[8px] font-black uppercase tracking-widest bg-orange-500 text-white px-1.5 py-0.5 rounded">URGENT</span>
            <span className="text-[8px] font-black uppercase tracking-widest bg-slate-700 text-slate-200 px-1.5 py-0.5 rounded">PLUMBING</span>
            <span className="text-[10px] font-bold text-white">Toilet leaking</span>
          </div>
          <div className="text-[9px] text-slate-500 mt-0.5">Jane · 2h ago</div>
        </div>
        <div className="p-2.5 space-y-1.5" style={{ background: `${accent}11` }}>
          <div className="text-[9px] uppercase font-black tracking-widest" style={{ color: accent }}>AI triage</div>
          <div className="text-[10px] text-slate-200 leading-snug">Active water leak from toilet base — same-day risk to flooring.</div>
          <div className="flex items-center gap-1 flex-wrap mt-1">
            <span className="text-[8px] font-mono bg-white/10 rounded px-1.5 py-0.5">$150–$350</span>
            <span className="text-[8px] font-mono bg-emerald-500/25 text-emerald-200 rounded px-1.5 py-0.5">Match: Mike Plumbing</span>
          </div>
          <div className="text-[8px] italic text-slate-400 mt-1">Told renter: shut off supply valve, place towels.</div>
        </div>
        <div className="p-2 flex items-center gap-1.5 border-t border-white/5">
          <div className="flex-1 h-7 rounded-md bg-emerald-500/20 border border-emerald-500/40 flex items-center justify-center gap-1 text-[9px] font-black uppercase tracking-widest text-emerald-200">
            <PaperPlaneTilt weight="fill" size={9}/> Dispatch Vendor
          </div>
          <div className="h-7 px-2 rounded-md bg-white/5 border border-white/10 flex items-center gap-1 text-[9px] font-black uppercase tracking-widest text-slate-300">
            <Wrench weight="fill" size={9}/> Approve
          </div>
        </div>
      </div>
      <div className="mt-2 bg-slate-950/60 border border-white/5 rounded-lg p-2.5 flex items-center gap-2">
        <div className="h-8 w-8 rounded-lg bg-emerald-500/20 flex items-center justify-center">
          <CreditCard weight="fill" size={14} className="text-emerald-400"/>
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[9px] font-black uppercase tracking-widest text-slate-500">Renter portal</div>
          <div className="text-[10px] text-slate-200 font-mono truncate">/rent/kNdrjgQ… · Pay rent · Report repair</div>
        </div>
        <div className="text-[9px] font-black uppercase tracking-widest text-emerald-400">Magic Link</div>
      </div>
    </div>
  </Frame>
);

// ─── Landscaping — Snow Dispatch + Route Optimizer ────────
const LandscapingMockup = ({ accent }) => (
  <Frame accent={accent} testid="mockup-landscaping">
    <div className="bg-gradient-to-br from-slate-900 to-slate-950 p-4 text-white">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="h-8 w-8 rounded-lg flex items-center justify-center" style={{ background: `${accent}22`, color: accent }}>
            <TreeEvergreen weight="fill" size={16}/>
          </div>
          <div>
            <div className="font-display font-black text-sm leading-none">Snow Dispatch</div>
            <div className="text-[9px] text-slate-400 mt-0.5">NWS · 48h forecast · Crew A</div>
          </div>
        </div>
        <div className="px-2 py-1 rounded text-[9px] font-black uppercase tracking-widest inline-flex items-center gap-0.5" style={{ background: `${accent}22`, color: accent }}>
          <Snowflake weight="fill" size={9}/> Dispatch
        </div>
      </div>
      <div className="grid grid-cols-3 gap-1.5 mb-3">
        <KPI label="Snow accts" value="3" accent={accent} testid="ls-kpi-snow"/>
        <KPI label="Stops today" value="6" accent="#22c55e" testid="ls-kpi-stops"/>
        <KPI label="Salt low" value="1" accent="#f59e0b" testid="ls-kpi-low"/>
      </div>
      <div className="bg-slate-950/60 border border-white/5 rounded-lg overflow-hidden">
        <div className="p-2.5 border-b border-white/5 flex items-center gap-1.5 flex-wrap">
          <span className="text-[8px] font-black uppercase tracking-widest bg-cyan-500 text-white px-1.5 py-0.5 rounded">STORM 6-9pm</span>
          <span className="text-[8px] font-black uppercase tracking-widest bg-slate-700 text-slate-200 px-1.5 py-0.5 rounded">2-4"</span>
          <span className="text-[10px] font-bold text-white">Chestnut HOA · Riverside Cafe · Warehouse</span>
        </div>
        <div className="p-2.5 space-y-1.5" style={{ background: `${accent}11` }}>
          <div className="text-[9px] uppercase font-black tracking-widest" style={{ color: accent }}>Draft crew SMS</div>
          <div className="text-[10px] text-slate-200 leading-snug italic">"Snow crew — 2-4" snow tonight 6-9pm. Salt trucks roll at 5am, plows on standby. 3 accounts on the list. Reply GO."</div>
        </div>
        <div className="p-2 flex items-center gap-1.5 border-t border-white/5">
          <div className="flex-1 h-7 rounded-md bg-emerald-500/20 border border-emerald-500/40 flex items-center justify-center gap-1 text-[9px] font-black uppercase tracking-widest text-emerald-200">
            <PaperPlaneTilt weight="fill" size={9}/> Send to Crew
          </div>
          <div className="h-7 px-2 rounded-md bg-white/5 border border-white/10 flex items-center gap-1 text-[9px] font-black uppercase tracking-widest text-slate-300">
            <Sparkle weight="fill" size={9}/> Optimize
          </div>
        </div>
      </div>
      <div className="mt-2 bg-slate-950/60 border border-white/5 rounded-lg p-2.5 flex items-center gap-2">
        <div className="h-8 w-8 rounded-lg bg-amber-500/20 flex items-center justify-center">
          <Package weight="fill" size={14} className="text-amber-400"/>
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[9px] font-black uppercase tracking-widest text-slate-500">Rock salt · North bin</div>
          <div className="text-[10px] text-slate-200 font-mono">0.5 / 2.0 tons</div>
        </div>
        <div className="text-[9px] font-black uppercase tracking-widest text-amber-400">LOW</div>
      </div>
    </div>
  </Frame>
);

// ─── Plumbing — AI Leak Diagnose ─────────────────────────────
const PlumbingMockup = ({ accent }) => (
  <Frame accent={accent} testid="mockup-plumbing">
    <div className="bg-gradient-to-br from-slate-900 to-slate-950 p-4 text-white">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="h-8 w-8 rounded-lg flex items-center justify-center" style={{ background: `${accent}22`, color: accent }}>
            <Drop weight="fill" size={16}/>
          </div>
          <div>
            <div className="font-display font-black text-sm leading-none">AI Leak Diagnose</div>
            <div className="text-[9px] text-slate-400 mt-0.5">1200 Sample St · Job #4821</div>
          </div>
        </div>
        <div className="px-2 py-1 rounded text-[9px] font-black uppercase tracking-widest inline-flex items-center gap-0.5" style={{ background: `${accent}22`, color: accent }}>
          <Sparkle weight="fill" size={9}/> Claude
        </div>
      </div>
      <div className="grid grid-cols-3 gap-1.5 mb-3">
        <KPI label="Jobs today" value="3" accent={accent} testid="p-kpi-jobs"/>
        <KPI label="Est. sent" value="$1.4k" accent="#22c55e" testid="p-kpi-est"/>
        <KPI label="Awaiting" value="1" accent="#f59e0b" testid="p-kpi-await"/>
      </div>
      {/* Photo strip */}
      <div className="flex gap-1.5 mb-2">
        {[Drop, Camera, Wrench].map((I, i) => (
          <div key={i} className="flex-1 aspect-square rounded-lg bg-slate-800 border border-white/5 flex items-center justify-center">
            <I weight="fill" size={22} className="text-slate-500"/>
          </div>
        ))}
      </div>
      {/* AI verdict */}
      <div className="bg-slate-950/60 border border-white/5 rounded-lg p-2.5 space-y-1.5" style={{ background: `${accent}11` }}>
        <div className="text-[9px] font-black uppercase tracking-widest" style={{ color: accent }}>Verdict</div>
        <div className="text-[11px] text-slate-200 leading-snug">
          <span className="font-black">Failed ¼-turn shut-off</span> — corrosion on inlet nut, drip trail on cabinet floor. Swap to ball valve + inspect trap.
        </div>
        <div className="text-[10px] text-slate-400 leading-snug">
          Parts: ½" SharkBite ball valve, PTFE tape, drip pan. Est <span className="text-white font-black">$285</span>.
        </div>
      </div>
      {/* Estimate SMS + Approve */}
      <div className="mt-2 bg-slate-950/60 border border-white/5 rounded-lg p-2 flex items-center gap-1.5">
        <div className="flex-1 h-7 rounded-md bg-emerald-500/20 border border-emerald-500/40 flex items-center justify-center gap-1 text-[9px] font-black uppercase tracking-widest text-emerald-200">
          <PaperPlaneTilt weight="fill" size={9}/> SMS Estimate
        </div>
        <div className="h-7 px-2 rounded-md bg-white/5 border border-white/10 flex items-center gap-1 text-[9px] font-black uppercase tracking-widest text-slate-300">
          <CheckCircle weight="fill" size={9}/> Approve
        </div>
      </div>
    </div>
  </Frame>
);

// ─── Electrical — Panel Schedule + AI Load-Calc ────────────
const ElectricalMockup = ({ accent }) => (
  <Frame accent={accent} testid="mockup-electrical">
    <div className="bg-gradient-to-br from-slate-900 to-slate-950 p-4 text-white">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="h-8 w-8 rounded-lg flex items-center justify-center" style={{ background: `${accent}22`, color: accent }}>
            <Lightning weight="fill" size={16}/>
          </div>
          <div>
            <div className="font-display font-black text-sm leading-none">Panel Schedule</div>
            <div className="text-[9px] text-slate-400 mt-0.5">3421 Girard · 200A · Sq D QO</div>
          </div>
        </div>
        <div className="px-2 py-1 rounded text-[9px] font-black uppercase tracking-widest inline-flex items-center gap-0.5" style={{ background: `${accent}22`, color: accent }}>
          <Sparkle weight="fill" size={9}/> AI Load-Calc
        </div>
      </div>
      <div className="grid grid-cols-3 gap-1.5 mb-3">
        <KPI label="Circuits" value="30" accent={accent} testid="e-kpi-circ"/>
        <KPI label="Load VA" value="18.2k" accent="#22c55e" testid="e-kpi-load"/>
        <KPI label="NEC flags" value="2" accent="#ef4444" testid="e-kpi-nec"/>
      </div>
      {/* Panel grid */}
      <div className="bg-slate-950/60 border border-white/5 rounded-lg p-2 mb-2 grid grid-cols-4 gap-1">
        {[
          {n:1,l:"Kitchen SBC",g:"AFCI",c:"#22c55e"},
          {n:2,l:"Kitchen GFCI",g:"GFCI",c:"#0ea5e9"},
          {n:3,l:"Range 40A",g:"2-P",c:"#f59e0b"},
          {n:4,l:"Range 40A",g:"2-P",c:"#f59e0b"},
          {n:5,l:"BR outlets",g:"AFCI",c:"#22c55e"},
          {n:6,l:"BR lts",g:"",c:"#64748b"},
          {n:7,l:"Bath GFCI",g:"GFCI",c:"#0ea5e9"},
          {n:8,l:"EV 60A",g:"2-P",c:"#eab308"},
        ].map(b => (
          <div key={b.n} className="rounded-md bg-slate-800 border border-white/5 p-1">
            <div className="flex items-center justify-between">
              <span className="text-[8px] font-mono text-slate-500">#{b.n}</span>
              {b.g && <span className="text-[7px] font-black uppercase tracking-wider px-1 rounded" style={{background:`${b.c}22`,color:b.c}}>{b.g}</span>}
            </div>
            <div className="text-[8px] font-black text-slate-200 leading-tight truncate mt-0.5">{b.l}</div>
          </div>
        ))}
      </div>
      {/* NEC-flagged finding */}
      <div className="rounded-lg p-2 space-y-1" style={{ background: `${accent}11`, border: `1px solid ${accent}33` }}>
        <div className="flex items-center gap-1.5">
          <Warning weight="fill" size={10} style={{ color: "#ef4444" }}/>
          <span className="text-[9px] font-black uppercase tracking-widest" style={{ color: accent }}>NEC 210.12 — needs AFCI</span>
        </div>
        <div className="text-[10px] text-slate-200 leading-snug">Bedroom circuit #6 lacks arc-fault protection. Swap breaker or move to combo AFCI/GFCI slot.</div>
      </div>
    </div>
  </Frame>
);

// ─── Handyman — Booking Radar + Tap-to-Pay ─────────────────
const HandymanMockup = ({ accent }) => (
  <Frame accent={accent} testid="mockup-handyman">
    <div className="bg-gradient-to-br from-slate-900 to-slate-950 p-4 text-white">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="h-8 w-8 rounded-lg flex items-center justify-center" style={{ background: `${accent}22`, color: accent }}>
            <Calendar weight="fill" size={16}/>
          </div>
          <div>
            <div className="font-display font-black text-sm leading-none">Today · One Truck</div>
            <div className="text-[9px] text-slate-400 mt-0.5">Booking Radar · 3 self-booked</div>
          </div>
        </div>
        <div className="px-2 py-1 rounded text-[9px] font-black uppercase tracking-widest inline-flex items-center gap-0.5" style={{ background: `${accent}22`, color: accent }}>
          <CreditCard weight="fill" size={9}/> Stripe
        </div>
      </div>
      <div className="grid grid-cols-3 gap-1.5 mb-3">
        <KPI label="Jobs" value="3" accent={accent} testid="h-kpi-jobs"/>
        <KPI label="Paid today" value="$625" accent="#22c55e" testid="h-kpi-paid"/>
        <KPI label="AI drafted" value="2" accent="#0ea5e9" testid="h-kpi-ai"/>
      </div>
      {/* Booking rail */}
      <div className="bg-slate-950/60 border border-white/5 rounded-lg overflow-hidden">
        {[
          {t:"9:00a", j:"Broken drawer + hinge", p:"$185", s:"paid", c:"#22c55e"},
          {t:"11:30a", j:"Mount 65\" TV", p:"$220", s:"en_route", c:"#0ea5e9"},
          {t:"2:00p", j:"Patch drywall — hallway", p:"$220", s:"booked", c:"#64748b"},
        ].map((r, i) => (
          <div key={i} className={`p-2 flex items-center gap-2 ${i>0?"border-t border-white/5":""}`}>
            <div className="h-9 w-11 rounded bg-slate-800 flex items-center justify-center text-[9px] font-black text-slate-300">{r.t}</div>
            <div className="flex-1 min-w-0">
              <div className="text-[10px] font-black text-white truncate">{r.j}</div>
              <div className="text-[9px] text-slate-500">Self-booked</div>
            </div>
            <div className="text-right">
              <div className="text-[10px] font-black text-white">{r.p}</div>
              <div className="text-[8px] font-black uppercase tracking-widest" style={{color:r.c}}>{r.s}</div>
            </div>
          </div>
        ))}
      </div>
      {/* Tap-to-Pay cue */}
      <div className="mt-2 flex items-center gap-1.5">
        <div className="flex-1 h-7 rounded-md bg-slate-800 border border-white/10 flex items-center justify-center gap-1 text-[9px] font-black uppercase tracking-widest text-slate-300">
          <Sparkle weight="fill" size={9}/> AI Estimate
        </div>
        <div className="flex-1 h-7 rounded-md border flex items-center justify-center gap-1 text-[9px] font-black uppercase tracking-widest" style={{ background: `${accent}22`, borderColor: `${accent}55`, color: accent }}>
          <CreditCard weight="fill" size={9}/> Tap to Charge
        </div>
      </div>
    </div>
  </Frame>
);

// ─── Vendors — Bridge Inbox + AI Bid Drafter ───────────────
const VendorsMockup = ({ accent }) => (
  <Frame accent={accent} testid="mockup-vendors">
    <div className="bg-gradient-to-br from-slate-900 to-slate-950 p-4 text-white">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="h-8 w-8 rounded-lg flex items-center justify-center" style={{ background: `${accent}22`, color: accent }}>
            <PlugsConnected weight="fill" size={16}/>
          </div>
          <div>
            <div className="font-display font-black text-sm leading-none">Bridge Inbox</div>
            <div className="text-[9px] text-slate-400 mt-0.5">4 PM sources · 12 open WOs</div>
          </div>
        </div>
        <div className="px-2 py-1 rounded text-[9px] font-black uppercase tracking-widest inline-flex items-center gap-0.5" style={{ background: `${accent}22`, color: accent }}>
          <Sparkle weight="fill" size={9}/> AI Bid
        </div>
      </div>
      <div className="grid grid-cols-3 gap-1.5 mb-3">
        <KPI label="Win rate" value="72%" accent={accent} testid="v-kpi-win"/>
        <KPI label="Open WOs" value="12" accent="#f59e0b" testid="v-kpi-open"/>
        <KPI label="Paid MTD" value="$18k" accent="#22c55e" testid="v-kpi-paid"/>
      </div>
      {/* PM-source stream */}
      <div className="bg-slate-950/60 border border-white/5 rounded-lg overflow-hidden">
        {[
          {src:"Buildium",c:"#0ea5e9",job:"Snake main line — 214 Chestnut",sla:"48h"},
          {src:"AppFolio",c:"#22c55e",job:"Ceiling patch after leak — 3421 Girard",sla:"24h"},
          {src:"Yardi",c:"#a855f7",job:"Common-area lighting swap",sla:"72h"},
        ].map((r, i) => (
          <div key={i} className={`p-2 flex items-center gap-2 ${i>0?"border-t border-white/5":""}`}>
            <div className="text-[8px] font-black uppercase tracking-widest px-1.5 py-0.5 rounded" style={{ background:`${r.c}22`, color:r.c }}>{r.src}</div>
            <div className="flex-1 min-w-0">
              <div className="text-[10px] font-black text-white truncate">{r.job}</div>
              <div className="text-[9px] text-slate-500 inline-flex items-center gap-0.5"><Clock size={8}/> SLA {r.sla}</div>
            </div>
            <div className="h-6 px-1.5 rounded-md border text-[8px] font-black uppercase tracking-widest inline-flex items-center gap-0.5"
                  style={{ background: `${accent}22`, borderColor: `${accent}55`, color: accent }}>
              <Sparkle weight="fill" size={8}/> Bid
            </div>
          </div>
        ))}
      </div>
      {/* Per-PM SLA scorecard bar */}
      <div className="mt-2 bg-slate-950/60 border border-white/5 rounded-lg p-2 space-y-1">
        <div className="text-[9px] font-black uppercase tracking-widest text-slate-500">Per-PM scorecard</div>
        {[
          {n:"Buildium",v:82,c:"#22c55e"},
          {n:"AppFolio",v:71,c:"#0ea5e9"},
          {n:"Yardi",v:44,c:"#f59e0b"},
        ].map((r, i) => (
          <div key={i} className="flex items-center gap-1.5">
            <div className="text-[9px] font-mono text-slate-400 w-14">{r.n}</div>
            <div className="flex-1 h-1.5 rounded-full bg-slate-800 overflow-hidden">
              <div className="h-full rounded-full" style={{ width:`${r.v}%`, background: r.c }}/>
            </div>
            <div className="text-[9px] font-mono text-slate-300 w-6 text-right">{r.v}%</div>
          </div>
        ))}
      </div>
    </div>
  </Frame>
);
